import React from "react";
import { HelpCircle, X } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
} from "@/components/ui/sheet";
import { HELP } from "@/lib/help";

/** Renders inline markdown-like text: **bold** and `code`. */
function renderInline(text) {
  if (!text) return null;
  const parts = [];
  let remaining = text;
  let key = 0;
  const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(<React.Fragment key={key++}>{text.slice(last, m.index)}</React.Fragment>);
    const tok = m[0];
    if (tok.startsWith("**")) {
      parts.push(<strong key={key++} className="text-foreground font-semibold">{tok.slice(2, -2)}</strong>);
    } else {
      parts.push(<code key={key++} className="font-mono text-primary text-[0.85em] bg-secondary px-1 py-0.5 rounded">{tok.slice(1, -1)}</code>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(<React.Fragment key={key++}>{text.slice(last)}</React.Fragment>);
  return parts;
  // unused 'remaining'
  void remaining;
}

export default function HelpButton({ topic, variant = "icon" }) {
  const doc = HELP[topic];
  if (!doc) return null;

  return (
    <Sheet>
      <SheetTrigger asChild>
        {variant === "icon" ? (
          <button
            data-testid={`help-button-${topic}`}
            className="inline-flex items-center justify-center w-8 h-8 rounded-sm border border-border bg-card hover:border-primary hover:text-primary text-muted-foreground transition-colors"
            aria-label={`Help — ${doc.title}`}
            title={`Help — ${doc.title}`}
          >
            <HelpCircle className="w-4 h-4" strokeWidth={2} />
          </button>
        ) : (
          <button
            data-testid={`help-button-${topic}`}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border border-border bg-card hover:border-primary hover:text-primary text-muted-foreground font-mono text-[10px] uppercase tracking-widest transition-colors"
          >
            <HelpCircle className="w-3.5 h-3.5" /> Help
          </button>
        )}
      </SheetTrigger>
      <SheetContent
        data-testid={`help-sheet-${topic}`}
        side="right"
        className="w-full sm:max-w-xl overflow-y-auto bg-card border-l border-border"
      >
        <SheetHeader className="space-y-1 mb-6">
          <div className="font-mono text-[10px] uppercase tracking-widest text-primary">
            Documentation
          </div>
          <SheetTitle className="font-display font-black uppercase text-2xl tracking-tight text-foreground">
            {doc.title}
          </SheetTitle>
          {doc.subtitle && (
            <SheetDescription className="text-muted-foreground text-sm">
              {doc.subtitle}
            </SheetDescription>
          )}
        </SheetHeader>

        {doc.intro && (
          <p className="text-sm text-foreground/90 leading-relaxed mb-6">
            {renderInline(doc.intro)}
          </p>
        )}

        <div className="space-y-6">
          {doc.sections?.map((s, i) => (
            <section key={i} className="border-l-2 border-primary/40 pl-4">
              <h4 className="font-display font-bold uppercase tracking-widest text-[11px] text-primary mb-2">
                {s.h}
              </h4>
              {s.p && (
                <p className="text-sm text-foreground/85 leading-relaxed">
                  {renderInline(s.p)}
                </p>
              )}
              {s.list && (
                <ul className="space-y-1.5 mt-1">
                  {s.list.map((item, j) => (
                    <li key={j} className="text-sm text-foreground/85 leading-relaxed flex gap-2">
                      <span className="text-primary mt-1.5">▸</span>
                      <span>{renderInline(item)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <div className="mt-8 pt-4 border-t border-border font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Frisbee/X · Tactic Cutter · Phase 1
        </div>
      </SheetContent>
    </Sheet>
  );
}
